import { useCallback, useMemo, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, History } from 'lucide-react';
import type { GridApi } from 'ag-grid-community';
import { ROTULO_CPM_ORIGEM, type CentroGradeResponse, type CpmCelulaResponse } from '@triade/contracts';
import { useAnaliticos, useAtualizarCentro, useGradeCpm, useGravarCpm, useVersoesCpm } from '@/api/centros-custo';
import { useMe } from '@/api/me';
import { ErroFormacao } from '@/components/formacao/avisos-formacao';
import { RecordToolbar, SaidaComAlteracoesDialog, useBlockerDirty } from '@/components/record-toolbar';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ApiError } from '@/lib/api';
import { formatarDataHora } from '@/lib/format';
import { chaveCelula, COL_MINUTOS, GradeCpm } from './grade-cpm';
import { competenciaAtual, cpmDigitado, cpmParaTexto, mesmoDecimal, minutosDigitado, rotuloCompetencia, somarMeses } from './tipos';

const MESES_NA_GRADE = 13;
const PASSO = 6;

function mensagemErro(e: unknown): string {
  return e instanceof ApiError ? `${e.message}${e.code ? ` (${e.code})` : ''}` : 'falha de comunicação com a API';
}

export interface CustoPorMinutoProps {
  /** Ultimo mes da janela (AAAA-MM); sem ele, o mes corrente. */
  ate?: string | undefined;
  onAte: (ate: string | undefined) => void;
}

/**
 * Aba "Custo por minuto": grade centro x mes. Quem tem `cpm.ver` ve e (com `cpm.editar`) edita o R$/min de cada mes; quem so tem
 * `centro.ver` (PCP) ve os centros e edita so os minutos disponiveis, sem nenhum R$ (a API nem envia). Salvar grava um lote:
 * cada mes alterado vira a proxima versao (append-only) e os minutos vao para o cadastro do centro.
 */
export function CustoPorMinuto({ ate, onAte }: CustoPorMinutoProps) {
  const me = useMe();
  const permissoes = me.data?.permissoes ?? [];
  const comCpm = permissoes.includes('cpm.ver');
  const editaCpm = permissoes.includes('cpm.editar');
  const editaMinutos = permissoes.includes('centro.editar');
  const hoje = competenciaAtual();
  const fim = ate ?? hoje;
  const grade = useGradeCpm({ ate: fim, meses: MESES_NA_GRADE });
  // So analiticos entram na grade: a lista vem de GET /centros-custo/analiticos (ordem natural pelo caminho); a grade traz os valores.
  const analiticos = useAnaliticos(false);
  const gravar = useGravarCpm();
  const atualizar = useAtualizarCentro();

  const [edicoes, setEdicoes] = useState<ReadonlyMap<string, string>>(() => new Map());
  const edicoesRef = useRef(edicoes);
  edicoesRef.current = edicoes;
  const gradeApi = useRef<GridApi<CentroGradeResponse> | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [salvoEm, setSalvoEm] = useState<Date | null>(null);
  const [salvando, setSalvando] = useState(false);
  const [foco, setFoco] = useState<{ centro: CentroGradeResponse; competencia: string } | null>(null);
  const [versoesAbertas, setVersoesAbertas] = useState(false);

  const dirty = edicoes.size > 0;
  const bloqueio = useBlockerDirty(dirty);
  const centros = useMemo(() => {
    const porId = new Map((grade.data?.centros ?? []).map((c) => [c.id, c]));
    return (analiticos.data ?? []).flatMap((a) => {
      const linha = porId.get(a.id);
      return linha ? [linha] : [];
    });
  }, [grade.data, analiticos.data]);
  const meses = grade.data?.meses ?? [];

  const editar = useCallback((centro: CentroGradeResponse, coluna: string, texto: string) => {
    const chave = chaveCelula(centro.id, coluna);
    const novo = new Map(edicoesRef.current);
    const atual = coluna === COL_MINUTOS ? centro.minutosDisponiveisMes : (centro.celulas?.[coluna]?.valor ?? null);
    const digitado = coluna === COL_MINUTOS ? minutosDigitado(texto) : cpmDigitado(texto);
    if (texto.trim() === '' || (digitado !== null && mesmoDecimal(digitado, atual))) novo.delete(chave);
    else novo.set(chave, texto.trim());
    edicoesRef.current = novo;
    setEdicoes(novo);
    setErro(null);
  }, []);

  async function salvar() {
    gradeApi.current?.stopEditing(); // confirma a edicao em curso (Ctrl+S dentro da celula)
    const pendentes = [...edicoesRef.current.entries()];
    if (pendentes.length === 0 || salvando) return;
    const invalidas = pendentes.filter(([chave, t]) => (chave.endsWith(`|${COL_MINUTOS}`) ? minutosDigitado(t) : cpmDigitado(t)) === null);
    if (invalidas.length > 0) {
      setErro('Há valores inválidos: corrija ou apague as células destacadas em vermelho');
      return;
    }
    setErro(null);
    setSalvando(true);
    try {
      const meses = pendentes.filter(([c]) => !c.endsWith(`|${COL_MINUTOS}`));
      const minutos = pendentes.filter(([c]) => c.endsWith(`|${COL_MINUTOS}`));
      if (meses.length > 0) {
        await gravar.mutateAsync({
          alteracoes: meses.map(([chave, t]) => {
            const [centroId, competencia] = chave.split('|') as [string, string];
            return { centroId, competencia, valor: cpmDigitado(t) as string, origem: 'MANUAL' as const };
          }),
        });
        const resto = new Map(edicoesRef.current);
        for (const [c] of meses) resto.delete(c);
        edicoesRef.current = resto;
        setEdicoes(resto);
      }
      for (const [chave, t] of minutos) {
        const [centroId] = chave.split('|') as [string];
        await atualizar.mutateAsync({ id: centroId, dto: { minutosDisponiveisMes: minutosDigitado(t) as string } });
        const resto = new Map(edicoesRef.current);
        resto.delete(chave);
        edicoesRef.current = resto;
        setEdicoes(resto);
      }
      setSalvoEm(new Date());
    } catch (e) {
      setErro(mensagemErro(e));
    } finally {
      setSalvando(false);
    }
  }

  function desfazerTudo() {
    gradeApi.current?.stopEditing(true);
    const vazio = new Map<string, string>();
    edicoesRef.current = vazio;
    setEdicoes(vazio);
    setErro(null);
  }

  const invalidas = useMemo(
    () => [...edicoes.entries()].filter(([chave, t]) => (chave.endsWith(`|${COL_MINUTOS}`) ? minutosDigitado(t) : cpmDigitado(t)) === null).length,
    [edicoes],
  );

  let corpo;
  if (grade.isError) corpo = <div className="p-4"><ErroFormacao erro={grade.error} onTentar={() => void grade.refetch()} /></div>;
  else if (analiticos.isError) corpo = <div className="p-4"><ErroFormacao erro={analiticos.error} onTentar={() => void analiticos.refetch()} /></div>;
  else if (!grade.data || !analiticos.data) corpo = <Mensagem papel="status">Carregando custo por minuto…</Mensagem>;
  else if (centros.length === 0) corpo = <Mensagem papel="status">Nenhum centro analítico ativo. Só analítico recebe CPM: crie-o na aba Centros, dentro de um sintético.</Mensagem>;
  else
    corpo = (
      <GradeCpm
        centros={centros}
        meses={meses}
        comCpm={comCpm}
        editaCpm={editaCpm}
        editaMinutos={editaMinutos}
        edicoes={edicoes}
        onEditar={editar}
        onFoco={(centro, competencia) => setFoco(centro && competencia ? { centro, competencia } : null)}
        apiRef={gradeApi}
      />
    );

  return (
    <div className="flex flex-col gap-4">
      {!comCpm && grade.data && (
        <p role="note" data-testid="aviso-sem-cpm" className="rounded-xl border border-border bg-line-soft px-4 py-3 text-small text-ink-body">
          Seu papel vê só os minutos disponíveis de cada centro, sem valores em R$.{editaMinutos ? ' Edite os minutos direto na grade.' : ''}
        </p>
      )}
      <section aria-label="Custo por minuto" className="rounded-2xl border border-border bg-card">
        {comCpm && (
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-line-soft px-4 py-3">
            <div className="flex items-center gap-2">
              <Button type="button" variant="outline" size="sm" disabled={dirty} onClick={() => onAte(somarMeses(fim, -PASSO))} aria-label="Meses anteriores">
                <ChevronLeft aria-hidden="true" className="size-4" /> Anteriores
              </Button>
              <span data-testid="janela-meses" className="text-small text-ink-secondary tabular">
                {meses.length > 0 ? `${rotuloCompetencia(meses[0] as string)} a ${rotuloCompetencia(meses[meses.length - 1] as string)}` : '—'}
              </span>
              <Button type="button" variant="outline" size="sm" disabled={dirty || fim >= hoje} onClick={() => onAte(somarMeses(fim, PASSO) > hoje ? undefined : somarMeses(fim, PASSO))} aria-label="Meses seguintes">
                Seguintes <ChevronRight aria-hidden="true" className="size-4" />
              </Button>
              {dirty && <span className="text-caption text-ink-muted">Salve ou desfaça antes de mudar o período.</span>}
            </div>
            <Button type="button" variant="outline" size="sm" disabled={!foco} onClick={() => setVersoesAbertas(true)} className="gap-1.5">
              <History aria-hidden="true" className="size-4" /> Versões do mês
            </Button>
          </div>
        )}
        {corpo}
        {grade.data && comCpm && (
          <p className="border-t border-line-soft px-4 py-2.5 text-caption text-ink-muted">
            R$ por minuto com até 10 casas. Corrigir um mês grava uma nova versão (a anterior fica no histórico). Origem: manual, importado do XARP ou rateio.
            {grade.data.ultimoMesSalvo ? ` Último mês salvo: ${rotuloCompetencia(grade.data.ultimoMesSalvo)}.` : ''}
          </p>
        )}
      </section>

      {invalidas > 0 && (
        <div role="alert" className="rounded-xl border border-destructive/30 bg-danger-bg px-4 py-3 text-small text-danger-ink">
          {invalidas === 1 ? '1 valor inválido' : `${invalidas} valores inválidos`}: use um número maior ou igual a zero (CPM com até 10 casas, minutos com até 6).
        </div>
      )}

      {(editaCpm || editaMinutos) && (
        <RecordToolbar
          modo="lote"
          dirty={dirty}
          saving={salvando}
          savedAt={salvoEm}
          erro={erro}
          canPrev={false}
          canNext={false}
          alteradosCount={edicoes.size}
          onSave={() => void salvar()}
          onUndo={desfazerTudo}
        />
      )}
      <SaidaComAlteracoesDialog {...bloqueio} />
      <VersoesDialog alvo={versoesAbertas && foco ? foco : null} onFechar={() => setVersoesAbertas(false)} />
    </div>
  );
}

function VersoesDialog({ alvo, onFechar }: { alvo: { centro: CentroGradeResponse; competencia: string } | null; onFechar: () => void }) {
  const q = useVersoesCpm(alvo?.centro.id, alvo?.competencia);
  return (
    <Dialog open={alvo !== null} onOpenChange={(o) => !o && onFechar()}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>
            Versões de {alvo ? `${alvo.centro.codigo} · ${rotuloCompetencia(alvo.competencia)}` : ''}
          </DialogTitle>
          <DialogDescription>Cada correção do mês é uma nova versão; as anteriores nunca são apagadas. A mais nova vale.</DialogDescription>
        </DialogHeader>
        {q.isError ? (
          <ErroFormacao erro={q.error} onTentar={() => void q.refetch()} />
        ) : !q.data ? (
          <p role="status" className="text-small text-ink-muted">Carregando versões…</p>
        ) : q.data.length === 0 ? (
          <p className="text-small text-ink-muted">Este mês ainda não tem CPM registrado.</p>
        ) : (
          <table className="w-full text-small">
            <thead>
              <tr className="border-b border-border text-left text-caption text-ink-muted">
                {['Versão', 'R$/min', 'Origem', 'Quando', 'Quem', 'Motivo'].map((c) => (
                  <th key={c} scope="col" className="px-2 py-1.5 font-medium">{c}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {q.data.map((v: CpmCelulaResponse, i) => (
                <tr key={v.versao} data-vigente={i === 0 ? 'true' : undefined} className={i === 0 ? 'border-b border-border bg-line-soft' : 'border-b border-border'}>
                  <td className="px-2 py-1.5 tabular">v{v.versao}{i === 0 ? ' (vale)' : ''}</td>
                  <td className="px-2 py-1.5 tabular">{cpmParaTexto(v.valor)}</td>
                  <td className="px-2 py-1.5">{ROTULO_CPM_ORIGEM[v.origem]}</td>
                  <td className="px-2 py-1.5 tabular">{formatarDataHora(v.criadoEm)}</td>
                  <td className="px-2 py-1.5">{v.criadoPor ?? '—'}</td>
                  <td className="px-2 py-1.5">{v.motivo ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Mensagem({ children, papel }: { children: React.ReactNode; papel?: 'status' }) {
  return (
    <p role={papel} className="px-5 py-10 text-center text-small text-ink-muted">
      {children}
    </p>
  );
}
